import { useEffect, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import Pill from '../common/Pill'
import { scrollIntoViewRespectingMotion } from '../../utils/scroll'
import { formatInt } from '../../utils/format'
import {
  FS, SUCHE_CHIP_ABZEICHEN_HOEHE_PX, SUCHE_CHIP_ABZEICHEN_MIN_BREITE_PX,
  SUCHE_CHIP_HOEHE_PX, SUCHE_CHIP_UEBERSTAND_PX, SUCHE_CHIP_ZEILENHOEHE,
  SUCHE_LUECKE_HALB, SUCHE_V2_RAND, SP,
} from '../../theme/tokens'

/**
 * SucheBereichChips — die Bereichsleiste der mobilen Suche (Suche mobil v2,
 * Stufe 2).
 *
 * @description
 *   EINE waagerecht rollende Zeile: „All", danach jede AKTIVE Art, am Ende
 *   „All filters" (oeffnet das Bereichsblatt). Sie ersetzt die
 *   Bereichsauswahl IN der Suchpille und die Gruppenkoepfe der Trefferliste:
 *   der Bereich wird gewaehlt, bevor gesucht wird, statt nachher zu sehen,
 *   welche Arten es gibt.
 *
 *   🔴 **Sie ist eine duenne Huelle um `Pill`, keine vierte Chip-Familie**
 *   (Plan §9.4/B4). `Pill` bringt alles, was die Zeile braucht: Label,
 *   Auswahlzustand (`selected` → `aria-pressed`, Rand traegt den Zustand),
 *   echtes `<button>` mit 44-px-Ziel bei grobem Zeiger, `nowrap` und eine
 *   glasfreie Grundflaeche. Was `Pill` NICHT mitbringt, liefert diese Huelle:
 *   die waagerechte Rollflaeche (`overflow-x`, `flexWrap: nowrap`).
 *   `FilterPillRow` bleibt unbenutzt — sie bringt `GLASS_PILL_SX` mit
 *   (99-px-Radius, Lichtkante, Schatten), also genau die Kaesten, die der
 *   Owner beanstandet hat.
 *
 *   🔴 **Die Zahl am Chip (Stufe 5, Weg A).** Sie kommt aus `art_gesamt`, dem
 *   Feld, das der Server seit Stufe 5 auf **jede** Zeile schreibt
 *   (`backend/search.py`, Zaehlkarte). Die Karte wird hier aus **allen** Zeilen
 *   gebaut, `max` je Art (W23/W25): ein Schluesseltreffer steht vorn und traegt
 *   keine eigene Zaehlung, und die zwei Suchpfade koennen fuer dieselbe Art
 *   verschiedene Zahlen liefern. „All" traegt die **Summe** der Kartenwerte.
 *
 *   🔴 **Unbekannt ist nie 0** (Regel 22 §3): fehlt `art_gesamt` (alter Server,
 *   Fehler, gescheiterter Zaehllauf) oder hat eine Art keinen Eintrag, traegt
 *   der Chip **keine** Zahl — nicht „0". Die Zahl ist eine **untere Schranke**,
 *   nicht die „echte" Trefferzahl (§5.2/W25).
 *
 *   🔴 **Der zugaengliche Name traegt die Zahl** („Orders, 145 results"), und
 *   zwar ueber `aria-label`: die Zahl steht als eigener Span IM Knopf, und zwei
 *   Textknoten werden nicht zuverlaessig mit Leerzeichen verbunden. Mit
 *   `aria-label` ist der Name exakt und englisch; ohne Zahl bleibt es beim
 *   blossen Etikett (`undefined` → kein Attribut).
 *
 *   🔴 **Stufe 6a: die Zahl ist ein KIND, kein `Pill.count`** (Weg A, §2.3).
 *   `Pill.count` rendert `opacity: 0.7`-Fließtext; die Vorlage verlangt ein
 *   gefuelltes 20-px-Abzeichen. Weg A haelt `Pill` byte-gleich und laesst die
 *   neun uebrigen `count`-Aufrufer unberuehrt. Das Abzeichen erscheint nur bei
 *   **endlicher Zahl > 0** und **nicht im gesperrten Zustand** (Regel 22 §3:
 *   unbekannt ist nie 0). Der statische `unbekannt`-Chip traegt deshalb
 *   `role="group"`: `aria-label` auf einem rollenlosen `<span>` ist nach
 *   ARIA 1.2 unzulaessig (W-R2-1 aus Stufe 5).
 *
 *   🔴 **Waehrend ein neuer Begriff laedt, bleibt die alte Zahl stehen** — die
 *   Liste zeigt in diesem Fenster ebenfalls noch die alten Zeilen, und Zahl und
 *   Liste duerfen einander nicht widersprechen. Beides wechselt zusammen mit
 *   der Antwort. Ohne vorherige Zeilen (erster Abruf) gibt es **keine** Zahl.
 *
 * @useWhen
 *   - Die Bereichsleiste der mobilen Suche (einziger Aufrufer:
 *     `SidebarAndHeaderBar.jsx`, mobiler Suchzweig)
 *
 * @dontUseFor
 *   - Desktop-Suche — dort ist die Bereichswahl eine `Select` im Kopf
 *   - Filterzeilen anderer Listen — das ist `FilterPillRow`
 *   - Status-Chips in Trefferzeilen — das ist `Pill` direkt
 *
 * @related
 *   - Pill — die Chipform selbst; hier nur als Kind gerendert
 *   - SearchBereichWahl — das Blatt, das „All filters" oeffnet
 *
 * @reuseHints
 *   - Eine zweite glasfreie Filterzeile wuerde diese Huelle TEILEN (dann mit
 *     JSDoc und Registry-Eintrag), nicht kopieren
 *
 * @props
 *   bereiche        Liste `{ kind, label, aktiv }` aus `ladeBereiche()`.
 *   wert            der GEDRUECKTE Bereich (`''` = All). 🔴 Das ist der
 *                   WIRKSAME Bereich, sobald die getippte Syntax greift — der
 *                   Aufrufer entscheidet das (`SidebarAndHeaderBar.jsx`,
 *                   R16-B1) und uebergibt ihn hier; die Leiste kennt weder
 *                   Syntax noch Antwortkoepfe.
 *   onChange        `(kind) => void` — setzt denselben Zustand wie das Blatt.
 *   onAlleFilter    oeffnet das Bereichsblatt.
 *   alleFilterOffen `aria-expanded` des Ausloesers.
 *   treffer         die Trefferzeilen der letzten Antwort (`mobileSearchResults`).
 *                   Traegt je Zeile `art_gesamt` (`{kind: n}`); fehlt das Feld,
 *                   bleibt jeder Chip ohne Zahl. Die Karte wird aus ALLEN Zeilen
 *                   gebaut, nicht aus der ersten (W23).
 *   syntaxHinweis   Text oder `null`. Gesetzt = die getippte Syntax gewinnt
 *                   gerade: dann sind die Umschalt-Chips GESPERRT (`disabled`,
 *                   alle ausser dem gedrueckten) und der Satz steht sichtbar
 *                   UNTER der Leiste. `null` = normales Verhalten. Die
 *                   `role="status"`-Region steht in beiden Faellen im Baum
 *                   (stabile `id`, nur der Text wechselt) und ist bei
 *                   gesetztem Satz das `aria-describedby` der Leiste.
 */
/**
 * Die `id` der Hinweis-Region. Sie ist stabil und steht an EINER Stelle: die
 * Region wird dauerhaft gerendert (Akte
 * `docs/backlog-auto-repair/2026-11-01-suche-chip-hinweis-ohne-describedby.md`)
 * und die Leiste verweist per `aria-describedby` auf sie, solange der Satz
 * steht.
 */
const HINWEIS_ID = 'suche-chip-syntax-hinweis-text'

/**
 * Die Chip-Schale (Suche mobil v2, Stufe 6a, §2.3).
 *
 * 🔴 **`Pill` bleibt unangetastet.** Die Vorlage-Optik wird LOKAL ueber `sx`
 * gesetzt: `Pill` wird in 75 Produktionsdateien importiert; ein neuer Standard
 * dort verschoebe die Fallansicht, die Rechnungsliste, das Bestandsmuster und
 * die Startseite (§2.3, gemessen 03.11.2026).
 *
 * `height` statt `minHeight`: `minHeight` ist ein Boden UNTER dem Inhalt, und
 * bei `FS.lg`/1,4 stuende der Inhalt auf 29 px — der Chip waere 31 hoch und
 * wuechse gegenueber 27,5, obwohl die Stufe eine Verkleinerung auf 28 zusagt.
 * Mit `lineHeight: 1,2` faellt der Inhalt auf genau 28,0 (18 + 8 + 2).
 */
const schaleGrund = (selected) => ({
  height: SUCHE_CHIP_HOEHE_PX,
  lineHeight: SUCHE_CHIP_ZEILENHOEHE,
  px: 1,
  fontSize: FS.lg,
  // Kein Rand, auch aktiv — der Zustand traegt allein die Fuellung.
  borderColor: 'transparent',
  bgcolor: selected ? 'action.hover' : 'transparent',
  color: selected ? 'text.primary' : 'text.secondary',
  // 🔴 Die 44-px-TIPPFLÄCHE (§2.3/B3, Regel 22 §8) — die Hausform des
  // Leeren-✕ (`SidebarAndHeaderBar.jsx`, `&::after`). NUR senkrecht: 28 + 8 + 8
  // = 44. Waagerecht keine Erweiterung, sonst ueberlappt die Flaeche die
  // Nachbarin (`gap = SP.chip` = 4 px). Der Ueberstand liegt vollstaendig in
  // der Polsterbox der Zeile (`pt: SUCHE_LUECKE_HALB` oben,
  // `pb: SUCHE_V2_RAND` unten) — sonst schneidet `overflowY: 'hidden'` ab.
  // Am ruhenden Panel gemessen (2026-11-04): Kapsel-Oberkante 80, Schnittkante
  // 72, der E3c-Messpunkt `cy − 21` = **73** — 1 CSS-px innerhalb der Kante.
  //
  // 🔴 **`-(UEBERSTAND + 1)px`, nicht `-UEBERSTAND`** (Nachbesserung 1,
  // 2026-11-04, am Browserlauf gemessen). `inset` rechnet gegen die
  // **Polsterbox** des Traegers, nicht gegen seine Randbox — und `Pill` traegt
  // `border: 1px solid` (`Pill.jsx:231`). Mit `-8px` waere die Trefferflaeche
  // 26 + 16 = **42** hoch und stuende nur 7 px ueber der sichtbaren Kapsel.
  // Mit `-9px` sind es 26 + 18 = **44** und 8 px ueber der Randbox — die
  // Zusage des Plans.
  // 🔴 **Nicht die Ursache des ersten `null`** (R15-W3, 2026-11-04): der
  // Browserlauf lieferte `null`, weil das Panel beim Oeffnen 60–250 ms still
  // **unterhalb** des Fensters stand (Nachbesserung 3, s. E3c) — nicht, weil
  // ein Messpunkt auf einer Kante lag. Die 9 bleibt als geometrische Zusage;
  // E3c tastet die Kante inzwischen ab, statt einen Punkt auf sie zu setzen.
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: `-${SUCHE_CHIP_UEBERSTAND_PX + 1}px 0`,
  },
})

/**
 * Dieselbe Schale fuer den INTERAKTIVEN Chip — plus der Medienblock.
 *
 * 🔴 **Pflicht, nicht Fleiss.** `Pill` setzt `minHeight: 44` unter
 * `[t.breakpoints.down('sm')]` (`Pill.jsx:314`, im Zweig `interactive &&`).
 * Emotion emittiert den Medienblock NACH der Grundregel, und `min-height`
 * schlaegt `height`. DERSELBE Schluessel, damit die Ueberschreibung greift —
 * nicht `(pointer: coarse)` (den Block gibt es am `Pill` nicht). Der
 * Caller-`sx` kommt zuletzt (`Pill.jsx:339`) und gewinnt.
 */
const chipSchale = (t, selected) => ({
  ...schaleGrund(selected),
  [t.breakpoints.down('sm')]: {
    height: SUCHE_CHIP_HOEHE_PX,
    minHeight: SUCHE_CHIP_HOEHE_PX,
  },
})

/**
 * Die Schale des `unbekannt`-Chips: er hat keinen `onClick`, rendert also
 * einen `<span>`; `Pill` setzt den Medienblock nur im interaktiven Zweig. Er
 * traegt `height` + `minHeight` deshalb OHNE Medienblock (§2.3, N1).
 */
const chipSchaleStatisch = (selected) => ({
  ...schaleGrund(selected),
  minHeight: SUCHE_CHIP_HOEHE_PX,
})

/** Das Trefferzahl-Abzeichen (Stufe 6a, Weg A: Kind statt `Pill.count`). */
const abzeichen = {
  // 🔴 PFLICHT: `height`/`minWidth` wirken auf einem NICHT-ersetzten
  // Inline-Element nicht, und `Box component="span"` setzt kein `display`.
  // Ohne diese Zeile misst E3b nie 20, und
  // `SUCHE_CHIP_ABZEICHEN_HOEHE_PX` waere ein Token ohne Wirkung.
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: SUCHE_CHIP_ABZEICHEN_HOEHE_PX,
  minWidth: SUCHE_CHIP_ABZEICHEN_MIN_BREITE_PX,
  // 🔴 4 px Abstand zum Label. `Pill`s `gap: 0.5` greift hier NICHT: das
  // Abzeichen steht IM Label-Span (`Pill.jsx`, `<Box component="span">{children}`),
  // nicht als eigenes Flex-Kind. Ohne diese Zeile klebte die Zahl am Wort.
  ml: 0.5,
  px: 0.5,
  // 🔴 Der Kapsel-Radius als STRING, nicht als Token (R16-W6, 2026-11-04): es
  // gibt keinen Kapsel-Token, und `999` als nackte Zahl waere in `sx` ein
  // Vielfaches von `shape.borderRadius` (6) = 5994 px. Dieselbe Hausform
  // schreibt `Pill.jsx:215` an seiner Kapsel; der Regel-22-Waechter zaehlt
  // numerische Werte, keine Strings, und ist hier gruen.
  borderRadius: '999px',
  // Volle Tinte, Ziffer in der Grundfarbe — die Umkehrung traegt beide Modi
  // ohne einen zweiten Wert. Kontrast hell 15,5/15,7:1, dunkel 17,9:1
  // (gerechnet, §2.3; gemessen von E11 im Browser).
  bgcolor: 'text.primary',
  color: 'background.default',
  fontSize: FS.xxs,
  fontWeight: 700,
  // Steht ausdruecklich hier, obwohl `Pill` es auf der Wurzel setzt: das
  // Abzeichen traegt die Zahl einer LAUFENDEN Suche und sie springt sonst.
  fontVariantNumeric: 'tabular-nums',
}

/**
 * Die Zaehlkarte aus allen Zeilen: `max(art_gesamt)` je Art (W23/W25).
 *
 * 🔴 `max`, nicht der erste Wert und nicht `sum`: ein Schluesseltreffer steht
 * vorn und traegt keine eigene Zaehlung, und die zwei Suchpfade (Volltext,
 * Teilwort) ueberlappen sich — `sum` zaehlte doppelt.
 *
 * Nur gemessene, positive Zahlen kommen in die Karte; eine fehlende oder
 * unbrauchbare Angabe erzeugt **keinen** Eintrag (und damit keine „0").
 */
export function zaehlkarteAus(treffer) {
  const karte = {}
  if (!Array.isArray(treffer)) return karte
  for (const zeile of treffer) {
    const eintrag = zeile && typeof zeile === 'object' ? zeile.art_gesamt : null
    if (!eintrag || typeof eintrag !== 'object') continue
    for (const [kind, n] of Object.entries(eintrag)) {
      if (typeof n === 'number' && Number.isFinite(n) && n > 0 && n > (karte[kind] ?? -1)) {
        karte[kind] = n
      }
    }
  }
  return karte
}

export default function SucheBereichChips({
  bereiche = [],
  wert = '',
  onChange,
  onAlleFilter,
  alleFilterOffen = false,
  syntaxHinweis = null,
  treffer = [],
  sx,
}) {
  // 🔴 Aus ALLEN Zeilen, nicht aus der ersten (W23). `useMemo`, weil die Karte
  // je Bild sonst neu entstuende und `Pill` bei jedem Tastendruck neu renderte.
  const karte = useMemo(() => zaehlkarteAus(treffer), [treffer])
  // 🔴 Leere Karte → `undefined`, nicht 0: ohne Messung traegt auch „All" keine
  // Zahl (Regel 22 §3). `sum` der Kartenwerte ist die in §5.4 verlangte Summe —
  // und wie jede Kartenzahl eine untere Schranke.
  const summe = Object.keys(karte).length
    ? Object.values(karte).reduce((a, b) => a + b, 0)
    : undefined
  const zahl = (kind) => (Number.isFinite(karte[kind]) ? karte[kind] : undefined)
  // Der zugaengliche Name traegt die Zahl — nur wenn es eine gibt. Ohne Zahl
  // bleibt der Name das blosse Etikett (`undefined` → kein Attribut).
  // 🔴 R16-W6 (Runde 1, 2026-11-03): Singular bei n === 1. Vorher las der
  // Screenreader „Orders, 1 results"; der Sichttext (nur die Ziffer) war
  // richtig, der Fehler existierte allein in der Ansage.
  const beschriftung = (label, n) => {
    if (!Number.isFinite(n)) return undefined
    // 🔴 R15-W5 (WCAG 2.5.3, 2026-11-04): der Name nennt die Zahl SO, wie das
    // Abzeichen sie zeigt (`formatInt` → „1.234", nicht „1234"). Vorher war
    // der Name die rohe Zahl und damit fuer Sprachsteuerung („Products 1.234")
    // nicht erreichbar; der Sichttext war schon formatiert.
    return `${label}, ${formatInt(n)} ${n === 1 ? 'result' : 'results'}`
  }
  // Nur AKTIVE Arten (Plan §10 Stufe 2). Eine abgeschaltete Art ist im Blatt
  // sichtbar und dort mit ihrem Grund versehen; in der Leiste waere sie ein
  // Knopf, der nichts liefert.
  const aktive = bereiche.filter((b) => b.aktiv !== false)
  // Der gewaehlte Bereich bleibt sichtbar, auch wenn die Liste ihn (noch) nicht
  // kennt — sonst stuende die Leiste auf „All", waehrend die Suche filtert
  // (dieselbe Regel wie `bereichsEtikett`: unbekannt ist nicht „All").
  const unbekannt = wert && !aktive.some((b) => b.kind === wert)

  // 🔴 R16-C1 (Nachbesserung 4, 2026-11-01): solange die getippte Syntax
  // gewinnt, ist ein Chip-Tipp WIRKUNGSLOS — die Auswahl wird zwar gesetzt,
  // aber erst sichtbar, wenn jemand den Text leert. Ein Knopf, der nichts tut,
  // und eine stille Zustandsänderung, die spaeter ueberrascht.
  //
  // Deshalb: die Umschalt-Chips sind GESPERRT (alle ausser dem gedrueckten —
  // der zeigt den wirksamen Bereich und ist keine Einladung), und der Satz
  // steht sichtbar darunter. 🔴 Ein `title`/`aria-description` allein reichte
  // NICHT: diese Leiste gibt es nur am Telefon, und dort zeigt kein Zeiger
  // einen Tooltip (Hausregel: keine Information nur im Tooltip).
  // „All filters" bleibt bedienbar — das Blatt erklaert denselben Zustand
  // ohnehin schon (`bereich-syntax-hinweis`).
  const gesperrt = !!syntaxHinweis

  // 🔴 R16-B2 (Nachbesserung 1, 2026-11-01): der GEDRUECKTE Chip kommt ins
  // Bild. Die Leiste ist eine Rollflaeche; wird der Bereich aus dem Blatt
  // gewaehlt, steht sie meist am Ende (dort haengt „All filters"), und der
  // gedrueckte Chip liegt links ausserhalb — auf dem Schirm ist dann KEIN
  // gedrueckter Chip zu sehen, obwohl die Liste verengt ist.
  // 🔴 `inline`/`block: 'nearest'` — nur so weit rollen, wie noetig: beim Mount
  // mit „All" (Chip schon sichtbar) bewegt sich nichts. `scroll.js` respektiert
  // `prefers-reduced-motion`; `scrollIntoView` selbst tut das NICHT.
  // 🔴 Gesucht wird der KNOPF mit `aria-pressed="true"`, nicht ueber eine Ref:
  // `Pill` ist kein `forwardRef`-Bauteil, und eine Ref darauf kaeme nie an.
  // Den Auswahlzustand traegt ohnehin genau dieses Attribut (`Pill.jsx`,
  // `aria-pressed={isToggle ? !!selected : undefined}`).
  const leisteRef = useRef(null)
  useEffect(() => {
    const gedrueckt = leisteRef.current?.querySelector('[aria-pressed="true"]')
    scrollIntoViewRespectingMotion(gedrueckt, { block: 'nearest', inline: 'nearest' })
  }, [wert])

  // 🔴 R16-W2: ein Tipp auf einen Bereichs-Chip nimmt dem Suchfeld den Fokus
  // NICHT. `mousedown` ist der Schritt, der den Fokus verschiebt — `click`
  // kommt danach. `preventDefault` haelt den Fokus, wo er war: hatte das Feld
  // ihn, behaelt es ihn (die Tastatur bleibt offen); war sie unten, bleibt sie
  // unten. KEIN `focus()`-Aufruf — der waere der zweite Weg zum selben Ziel
  // und wuerde die Tastatur AUFZIEHEN, wo sie vorher zu war.
  // ⚠️ Gilt fuer die Umschalt-Chips, NICHT fuer „All filters": der Knopf
  // oeffnet das Blatt, und das holt den Fokus beim Schliessen selbst ins Feld
  // (`onGeschlossen`).
  const halteFokus = (e) => e.preventDefault()

  return (
    <>
      <Box
        ref={leisteRef}
        data-testid="suche-chip-zeile"
        role="group"
        aria-label="Search area"
        // 🔴 Akte `2026-11-01-suche-chip-hinweis-ohne-describedby.md`: die
        // gesperrten Chips fallen nativ aus der Tab-Reihenfolge, wer
        // durchgeht, landet direkt auf „All filters" und kann den Satz
        // verpassen. Die Leiste nennt ihn deshalb als Beschreibung — nur
        // solange er steht (`undefined` sonst, sonst haenge die Leiste an
        // einer leeren Region).
        aria-describedby={gesperrt ? HINWEIS_ID : undefined}
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: SP.chip,
          // Die Rollflaeche. `nowrap` ist die Bedingung dafuer, dass die Zeile
          // EINE bleibt (V2.1); ohne sie bricht sie bei 13 Arten um.
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          px: SUCHE_V2_RAND,
          // 🔴 Stufe 6a (§2.3/B3): die 16-px-Luecke zum Feld wird 8/8 geteilt.
          // Ohne `pt` liegt der obere Ueberstand der Tippflaeche AUSSERHALB der
          // Polsterkante, und `overflowY: 'hidden'` schneidet ihn ab — ein
          // abgeschnittener Bereich ist nicht mehr zu treffen.
          //
          // 🔴 **Das Polster ist GENAU der Ueberstand (8 px), keine „Luft“
          // darueber.** Nachbesserung 2 (2026-11-04) hatte 4 px addiert und mit
          // `mt: -4px` gegengerechnet; die Messung am Kopf hat das
          // **zurueckgenommen**: die Ursache des roten Laufs war NICHT diese
          // Kante, sondern das Panel, das still bei `translateY(844px)` stand,
          // waehrend `visible`/`stable` schon erfuellt waren (Befund
          // `docs/features/search.md` §Nachbesserung 3). Am ruhenden Ziel
          // gemessen: Kapsel-Oberkante 80, Schnittkante 72, Messpunkt
          // `cy − 21` = 73 — **1 CSS-px innerhalb**, eine ganze Kante ohne
          // Bruchteil, kein Zufall.
          pt: SUCHE_LUECKE_HALB,
          pb: SUCHE_V2_RAND,
          minHeight: SUCHE_CHIP_HOEHE_PX,
          // Waagerecht wischen rollt die Leiste; senkrecht bleibt beim Panel
          // (die Liste darunter traegt `pan-y`).
          touchAction: 'pan-x',
          pointerEvents: 'auto',
          ...sx,
        }}
      >
        <Pill
          selected={!wert}
          onClick={() => onChange?.('')}
          onMouseDown={halteFokus}
          // „All" ist der Umschalter auf den LEEREN Bereich. Bei wirksamer
          // Syntax ist auch er gesperrt — es sei denn, er IST der gedrueckte
          // (dann zeigt er den wirksamen Zustand und ist keine Einladung).
          disabled={gesperrt && !!wert}
          nowrap
          // Die Summe ueber alle Arten (§5.4 Punkt 1) — nicht die Zahl der
          // gerade gewaehlten. Ohne Karte beides `undefined` (keine Zahl).
          aria-label={beschriftung('All', summe)}
          sx={(t) => ({ flexShrink: 0, ...chipSchale(t, !wert) })}
        >
          All
          {!gesperrt && Number.isFinite(summe) && summe > 0 && (
            <Box component="span" data-testid="suche-chip-zahl" sx={abzeichen}>
              {formatInt(summe)}
            </Box>
          )}
        </Pill>
        {unbekannt && (
          // 🔴 R16-W5 + R15-W2 (Nachbesserung 1): KEIN Knopf mehr. Der Wert steht
          // hier, weil die Liste ihn (noch) nicht kennt — er ist eine AUSKUNFT,
          // keine Aktion. Bis hierher war das `selected` + `onClick={() => {}}`:
          // ein Tab-Stopp, der per Rolle eine Aktion ankuendigte und keine hatte.
          // Ohne `onClick` rendert `Pill` einen `<span>` (`Pill.jsx`,
          // `component={interactive ? 'button' : 'span'}`) — kein Fokusziel, keine
          // Rolle, kein `aria-pressed`. Der Auswahl-RING bleibt (`selected` wirkt
          // unabhaengig von der Klickbarkeit), die Aussage also sichtbar.
          // 🔴 R16-W-N1 (Nachbesserung 4): die 44-px-Hoehe traegt bei `Pill` nur
          // der interaktive Zweig (`Pill.jsx`, `minHeight` unter
          // `interactive &&`). Ohne `onClick` faellt der Span auf die reine
          // Pill-Geometrie (≈ 27,5 px) und steht sichtbar kleiner zwischen
          // 44-px-Nachbarn; die Hoehe wird deshalb hier gepinnt.
          <Pill
            selected
            nowrap
            // 🔴 W-R2-1 (aus Stufe 5 uebergeben): ein `aria-label` auf einem
            // rollenlosen `<span>` ist nach ARIA 1.2 UNZULAESSIG — der Name
            // ginge verloren. Seit Stufe 6a die Zahl als KIND rendert, waere
            // der Inhalt „Orders" + „145" ausserdem ohne Trennung. Deshalb
            // bekommt der statische Chip eine BENENNBARE Rolle (`group`) und
            // behaelt `aria-label`; der Name ist damit derselbe wie an den
            // Knoepfen („Orders, 145 results"), ohne Doppelansage.
            role="group"
            // 🔴 R16-W7 (Runde 1, 2026-11-03): derselbe zugaengliche Name wie an
            // den anderen Chips. Der Sichttext ist `wert`, also traegt der Name
            // ihn in gleicher Schreibweise (WCAG 2.5.3).
            aria-label={beschriftung(wert, zahl(wert))}
            sx={{ flexShrink: 0, ...chipSchaleStatisch(true) }}
          >
            {wert}
            {!gesperrt && Number.isFinite(zahl(wert)) && zahl(wert) > 0 && (
              <Box component="span" data-testid="suche-chip-zahl" sx={abzeichen}>
                {formatInt(zahl(wert))}
              </Box>
            )}
          </Pill>
        )}
        {aktive.map((b) => (
          <Pill
            key={b.kind}
            selected={wert === b.kind}
            onClick={() => onChange?.(b.kind)}
            onMouseDown={halteFokus}
            disabled={gesperrt && wert !== b.kind}
            nowrap
            // Die Zahl gehoert zum zugaenglichen Namen („Orders, 145 results");
            // `Pill` setzt `count` als eigenen Textknoten, und zwei Textknoten
            // werden nicht zuverlaessig mit Leerzeichen verbunden.
            aria-label={beschriftung(b.label, zahl(b.kind))}
            sx={(t) => ({ flexShrink: 0, ...chipSchale(t, wert === b.kind) })}
          >
            {b.label}
            {!gesperrt && Number.isFinite(zahl(b.kind)) && zahl(b.kind) > 0 && (
              <Box component="span" data-testid="suche-chip-zahl" sx={abzeichen}>
                {formatInt(zahl(b.kind))}
              </Box>
            )}
          </Pill>
        ))}
        {/* „All filters" oeffnet das Blatt. KEIN `selected`: der Knopf ist kein
            Umschalter, er oeffnet eine Liste — `aria-expanded` sagt das, und ein
            `aria-pressed` daneben verspraeche einen Zustand, den es nicht gibt.
            🔴 Er bleibt auch bei wirksamer Syntax BEDIENBAR: das Blatt zeigt
            denselben Zustand (`bereich-syntax-hinweis`) und ist der Weg zu den
            abgeschalteten Arten. */}
        <Pill
          onClick={onAlleFilter}
          nowrap
          aria-haspopup="listbox"
          aria-expanded={!!alleFilterOffen}
          // 🔴 Der Chip ist der AUSLOESER des Blatts und zaehlt fuer dessen
          // „Tippen daneben" nicht als ausserhalb (M3.9d): ohne diese Marke
          // schloesse der `pointerdown` des Chips das Blatt, der `click` laese
          // danach `false` und oeffnete es wieder — der zweite Klick taete nichts.
          data-blatt-ausgenommen="true"
          sx={(t) => ({ flexShrink: 0, ...chipSchale(t, false) })}
        >
          All filters
        </Pill>
      </Box>
      {/* 🔴 R16-C1 (Nachbesserung 4): der Satz steht als GESCHWISTER UNTER der
          Rollflaeche, nicht in ihr — die Leiste rollt waagerecht
          (`overflowX: 'auto'`); ein Kind darin wanderte beim Wischen mit und
          waere bei jeder nicht-leeren Auswahl ausserhalb des Bildes. Er kostet
          Hoehe nur in diesem Zustand (getippte Syntax gewinnt). `role="status"`
          meldet ihn auch Screenreadern; der Text kommt aus EINER Quelle
          (`SYNTAX_HINWEIS_TEXT`, `SearchBereichWahl.jsx`) — dieselbe Zeile, die
          das Blatt zeigt. */}
      {/* 🔴 Akte `2026-11-01-suche-chip-hinweis-ohne-describedby.md`: die
          Region steht DAUERHAFT im Baum und nur ihr TEXT wechselt. Eine
          Live-Region, die zusammen mit ihrem Inhalt in den Baum kommt, wird
          nicht zuverlaessig angesagt — der Screenreader hat den Knoten dann
          noch nicht beobachtet. Ohne Text kostet sie keine Hoehe (kein
          Polster, kein Inhalt) und bleibt fuer `aria-describedby` erreichbar. */}
      <Box
        id={HINWEIS_ID}
        role="status"
        data-testid="suche-chip-syntax-hinweis"
        sx={{
          flexShrink: 0,
          // Polster nur, wenn der Satz steht — sonst waere die leere Region
          // eine Luecke unter der Leiste.
          ...(gesperrt ? { px: SUCHE_V2_RAND, pb: SUCHE_V2_RAND } : null),
          fontSize: FS.xs,
          color: 'text.secondary',
        }}
      >
        {syntaxHinweis || ''}
      </Box>
    </>
  )
}
